import React, { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { Progress } from "@/components/ui/Progress";
import { sendRequest } from "../api";
import { 
  Copy, 
  CheckCircle2, 
  Gift, 
  DollarSign,
  Users
} from "lucide-react";

export const ReferralModal = ({ 
  open, 
  onOpenChange, 
  referralCode,
}) => {
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({
    currentMonthReferrals: 0,
    monthlyQuota: 1000,
    totalEarned: 0
  });

  useEffect(() => {
    if (open && referralCode) {
      fetchReferralStats();
      fireConfetti();
    }
  }, [open, referralCode]);

  const fetchReferralStats = async () => {
    try {
      const response = await sendRequest('/referrals', 'GET');
      const monthlyReferrals = response.current_month_referrals || 0;
      const totalEarned = Math.floor(monthlyReferrals / 3) * 5; // $5 for every 3 referrals

      setStats({
        currentMonthReferrals: monthlyReferrals,
        monthlyQuota: 1000,
        totalEarned
      });
    } catch (error) {
      console.error('Failed to fetch referral stats:', error);
    }
  };

  const fireConfetti = () => {
    // Fire multiple confetti bursts
    const count = 200;
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 100,
      spread: 50,
      startVelocity: 30,
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      });
    }

    // Fire confetti from both sides
    fire(0.25, {
      spread: 26,
      startVelocity: 55,
      origin: { x: 0.2, y: 0.7 },
      colors: ['#4F46E5', '#7C3AED', '#60A5FA']
    });
    fire(0.25, {
      spread: 26,
      startVelocity: 55,
      origin: { x: 0.8, y: 0.7 },
      colors: ['#4F46E5', '#7C3AED', '#60A5FA']
    });

    // Fire confetti in the middle
    fire(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8,
      origin: { x: 0.5, y: 0.7 },
      colors: ['#4F46E5', '#7C3AED', '#60A5FA']
    });
  };

  const referralLink = `${window.location.origin}/T-${referralCode}`;
  const progressToNextBonus = stats.currentMonthReferrals % 3;
  const referralsNeeded = 3 - progressToNextBonus;
  const maxEarnings = Math.floor(1000 / 3) * 5; // Max earnings based on 1000 monthly limit

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] overflow-hidden rounded-2xl border bg-white shadow-xl">
        <DialogHeader>
          <DialogTitle className="text-2xl text-center font-bold bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] text-transparent bg-clip-text">
            Earn Over $1,000/Month
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Marketing Message */}
          <div className="space-y-2 text-center">
            <p className="text-lg font-semibold text-gray-800">
              Every friend gets $2/mo off for life!
            </p>
            <p className="text-sm text-gray-600">
              For every 3 friends, you get $5. Up to 1000 friends/month
            </p>
          </div>

          {/* Monthly Progress */}
          <div className="bg-gray-50 p-4 rounded-xl space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-base font-medium text-gray-800">Monthly Progress</span>
              <Badge variant="secondary" className="text-sm px-3 rounded-full">
                {stats.currentMonthReferrals} / {stats.monthlyQuota}
              </Badge>
            </div>
            <div className="text-sm text-gray-700 flex items-center justify-between">
              <span>This month's earnings: ${stats.totalEarned}</span>
              <span className="text-blue-600 font-medium">Max: ${maxEarnings}</span>
            </div>
            <Progress 
              value={(stats.currentMonthReferrals / stats.monthlyQuota) * 100} 
              className="h-2 rounded-full"
            />
          </div>

          {/* Next Bonus Progress */}
          <div className="bg-blue-50 p-4 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-blue-500" />
              <span className="text-base font-medium text-gray-800">Next $5 bonus in:</span>
            </div>
            <Badge className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
              {referralsNeeded} more {referralsNeeded === 1 ? 'friend' : 'friends'}
            </Badge>
          </div>

          {/* Share Link */}
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={referralLink}
                readOnly
                className="text-sm font-medium border-2 rounded-xl"
              />
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleCopy}
                className={`${copied ? "text-green-600" : ""} border-2 rounded-xl`}
              >
                {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-sm text-center text-gray-600">
              Share your link to start earning
            </p>
          </div>

          {/* Terms */}
          <div className="text-sm text-gray-500 space-y-1.5">
            <p>• Resets monthly</p>
            <p>• One-time payment for each referral</p>
            <p>• Void if account cancelled/banned</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 